import { APPROVAL_STATUSES } from "../../utils/enums";

export var APPROVAL_COLOURS = {
  Pending: { bg: "#F5F5F5", text: "#555", border: "#DDD" },
  Approved: { bg: "#E8F5E9", text: "#2E7D32", border: "#A5D6A7" },
  "Needs Info": { bg: "#FFF3E0", text: "#E65100", border: "#FFCC80" },
  Declined: { bg: "#FFEBEE", text: "#C62828", border: "#EF9A9A" },
};

// ─────────────────────────────────────────────────────────────────────────────
// SectionTitle — bold uppercase section heading
// ─────────────────────────────────────────────────────────────────────────────
export function SectionTitle({ children }) {
  return (
    <h3 className="mb-3 text-xs text-zephyr-muted font-bold uppercase tracking-[0.5px]">
      {children}
    </h3>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ROField — read-only labelled field (key/value display)
// ─────────────────────────────────────────────────────────────────────────────
// Props: label, value, span (grid column span; default 1)
// boxStyle: optional inline style merged onto the value box — used to cap the box
// width (e.g. the small shoes Qty/Cost boxes) so the box stays narrow while its
// grid column can still be wider (letting the Leather pair start mid-panel).
export function ROField({ label, value, span = 1, nowrap = false, boxStyle }) {
  if (value === null || value === undefined || value === "") return null;
  // nowrap: keep the value on one line (used in the fixed-width fee Type/Amount
  // cells so a long value doesn't wrap the row). The box still fills its column,
  // so every box in the column is the same width.
  const valueCls =
    "text-sm text-zephyr-text bg-zephyr-off-white border border-zephyr-border rounded-[7px] px-3 py-2 min-h-9 flex items-center" +
    (nowrap ? " whitespace-nowrap" : "");
  return (
    // span is runtime-dynamic so we keep inline style for gridColumn only
    <div style={{ gridColumn: `span ${span}` }}>
      <div className="text-[11px] text-zephyr-muted font-bold uppercase tracking-[0.4px] mb-1">
        {label}
      </div>
      <div className={valueCls} style={boxStyle}>
        {value}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RODivider — horizontal divider with optional label
// ─────────────────────────────────────────────────────────────────────────────
// Props: label (optional)
export function RODivider({ label }) {
  return (
    <div className="col-span-4 flex items-center gap-[10px] mt-2 mb-1">
      <div className="h-px flex-1 bg-zephyr-border" />
      <span className="text-xs font-bold text-zephyr-muted uppercase tracking-[0.5px] whitespace-nowrap">
        {label}
      </span>
      <div className="h-px flex-1 bg-zephyr-border" />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ROSubtotal — labelled right-aligned monetary subtotal row
// ─────────────────────────────────────────────────────────────────────────────
// Props: label, value (already formatted)
export function ROSubtotal({ label, value }) {
  return (
    <div className="col-span-4 flex justify-end items-center gap-3 py-[6px] border-t border-zephyr-border mt-1">
      <span className="text-[13px] text-zephyr-muted font-semibold">{label}</span>
      <span className="text-[15px] font-bold text-zephyr-teal-dark">{value}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ChildPanel — bordered card wrapping one child's display
// ─────────────────────────────────────────────────────────────────────────────
// Props: index (0-based), colour (accent), children
export function ChildPanel({ index, colour, children }) {
  return (
    <div className="border border-zephyr-border rounded-[10px] mb-3 overflow-hidden">
      {/* background colour is a runtime prop — inline style for that value only */}
      <div
        style={{ background: colour || "#00695C" }}
        className="text-white py-[9px] px-4 font-bold text-sm"
      >
        Child {index + 1}
      </div>
      <div className="p-4">
        <div className="grid grid-cols-4 gap-3">{children}</div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ApprovalBadge — coloured status pill
// ─────────────────────────────────────────────────────────────────────────────
// Props: status — one of "Pending", "Approved", "Declined", "Needs Info"
export function ApprovalBadge({ status }) {
  var s = status || APPROVAL_STATUSES.PENDING;
  var col = APPROVAL_COLOURS[s] || APPROVAL_COLOURS.Pending;
  return (
    // bg/text/border colours come from APPROVAL_COLOURS at runtime — inline style for those only
    <span
      style={{ background: col.bg, color: col.text, borderColor: col.border }}
      className="border rounded-[5px] py-0.5 px-[9px] text-[11px] font-bold whitespace-nowrap"
    >
      {s}
    </span>
  );
}
